import { useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useClientAuth } from '../hooks/useClientAuth';
import { Bell, CircleHelp, MessageSquare, Menu } from 'lucide-react';
import { ClientHelpPanel } from '../components/ClientHelp';
import { EntitySwitcher } from './EntitySwitcher';
import { useFeedback } from '@babomrs/ui';

/**
 * Client portal topbar — navigation hamburger (below `md`), firm logo (links to
 * portal home), contextual Help, client name, notifications bell.
 *
 * Responsive behaviour follows `.claude/reference/frontend-ui-system.md`
 * § Responsive breakpoints and § Mobile navigation pattern (both binding): below
 * `md` the sidebar is off-canvas and is opened from here; at `md` and above the
 * rail is visible inline, so the hamburger is not rendered.
 */
interface ClientTopbarProps {
  /** Drawer state, for `aria-expanded`. */
  navOpen?: boolean;
  /** Toggles the off-canvas nav. Omit and no hamburger renders. */
  onToggleNav?: () => void;
  /** The shell holds this so it can restore focus here when the drawer closes. */
  hamburgerRef?: RefObject<HTMLButtonElement>;
}

export function ClientTopbar({ navOpen = false, onToggleNav, hamburgerRef }: ClientTopbarProps) {
  const { user } = useClientAuth();
  const { openFeedback } = useFeedback();
  const [helpOpen, setHelpOpen] = useState(false);

  // Runtime config from /config.js (injected by server.js per env).
  // Per frontend-ui-system.md: client portal badge shows in DEV/STAGING, hidden in PROD.
  const envLabel = window.__BABOMRS_CONFIG__?.envLabel;
  const showEnvBadge = envLabel && envLabel !== 'PROD';

  return (
    <header className="h-14 bg-[var(--surface)] border-b border-[var(--border)] flex items-center px-4 sticky top-0 z-10">
      <div className="flex items-center gap-2">
        {/* Hamburger — below `md` only, where the sidebar is off-canvas. At `md` and
            above the icon rail is inline, so there is nothing to open. */}
        {onToggleNav && (
          <button
            ref={hamburgerRef}
            type="button"
            onClick={onToggleNav}
            className="md:hidden w-11 h-11 -ml-2 flex items-center justify-center rounded-md text-[var(--text3)] hover:text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            aria-label="Open navigation"
            aria-expanded={navOpen}
            aria-controls="portal-nav"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        {/*
          The brand is the way back to the portal home, kept from #16982. It is no
          longer the ONLY way back: ClientPortalNav now carries every destination
          (#18259), which is the durable fix #16982 recorded as separate work.
        */}
        <Link
          to="/portal"
          className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          aria-label="Go to portal home"
        >
          <div className="w-8 h-8 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-xs font-medium">
            BA
          </div>
          <span className="text-sm font-medium text-[var(--text1)] hidden sm:inline">B &amp; A Accounting</span>
        </Link>
        {showEnvBadge && (
          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
            envLabel === 'DEV' ? 'bg-green-600 text-white' :
            envLabel === 'STAGING' ? 'bg-amber-500 text-white' :
            'bg-slate-500 text-white'
          }`}>{envLabel}</span>
        )}
      </div>

      {/* Entity switcher (spec §7) — between the brand and the spacer, as the prototype's CTOP places it.
          Fills the slot #18259 reserved for `_project/client-portal-enhancements.md` item U. */}
      {user && <EntitySwitcher />}

      <div className="flex-1" />

      {user && (
        <div className="flex items-center gap-3">
          {/* Help — contextual guidance for the current screen. frontend-ui-system.md
              § In-app help system is binding: the button must be visible on every
              screen, in a consistent position. Order matches the portal prototype. */}
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="flex items-center gap-1.5 p-1 text-[var(--text3)] hover:text-[var(--text1)] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            aria-label="Help for this page"
          >
            <CircleHelp className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Help</span>
          </button>
          {/* Feedback — opens the shared slide-out (Story D / Epic #9653) */}
          <button
            type="button"
            onClick={() => openFeedback()}
            className="flex items-center gap-1.5 p-1 text-[var(--text3)] hover:text-[var(--text1)]"
            aria-label="Send feedback"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Feedback</span>
          </button>
          {/* Notification bell */}
          <button className="relative p-1 text-[var(--text3)] hover:text-[var(--text1)]" aria-label="Notifications">
            <Bell className="w-5 h-5" />
            {(user.pendingSignatures + user.pendingQueries) > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[var(--error)] text-white rounded-full text-[10px] flex items-center justify-center">
                {user.pendingSignatures + user.pendingQueries}
              </span>
            )}
          </button>
          {/* Below sm the person's name gives way to the entity switcher, which is what scopes the page. */}
          <span className="hidden sm:inline text-sm text-[var(--text2)]">{user.displayName}</span>
          <ClientHelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
      )}
    </header>
  );
}
